import PropTypes from 'prop-types';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { V1Table as Table } from '@uhg-abyss/web/ui/Table';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';
import { API } from '@src/api';
import { MemberSearchResult } from '@src/api.types';
import { TMemberInfo } from '@repo/utils';
import { atom,useAtom } from 'jotai';
import React, { ReactElement } from 'react';
import { memberInContextAtom } from '@src/global-state';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { capitalizeName } from '@repo/utils';

export type DisplayResultsParams = {
  // "plan" if search was for member ID, "info" if search was for name & DOB
  forSearchTerm: 'plan' | 'info';
};

// Headings for results table
const columns = [
  { name: 'Name', key: 'name' },
  { name: 'Member ID', key: 'memberid' },
  { name: 'Date of birth', key: 'dateofbirth' },
  { name: 'State', key: 'state' },
  { name: 'Zip code', key: 'zipcode' },
  { name: 'Line of business', key: 'lob' },
  { name: 'Action', key: 'action' },
];

type TableShape = {
  name: string;
  memberid: string;
  dateofbirth: string;
  state: string;
  zipcode: string;
  lob: string;
  action: ReactElement;
};

export type ErrorDuringSearch = null;

export type NoSearchPerformed = undefined;

export const memberSearchResultsAtom = atom<
  MemberSearchResult[] | NoSearchPerformed | ErrorDuringSearch
>(undefined as NoSearchPerformed);

const WrapperDiv = styled('div', { overflow: 'auto' });
export const DisplayResults = ({ forSearchTerm }: DisplayResultsParams) => {
  // Member in context is our global state, the member currently being assisted by our user.
  // We don't need to view the value here, only set the value when the user selects a member from results table.
  const [memberInContext, setMemberInContext] = useAtom(memberInContextAtom);
  const [isLoading, setIsLoading] = React.useState(false);

  const [
    memberSearchResults, // Value of the results, array of results or null if no search has been performed
    setMemberSearchResults, // Setter function, which will be used to set the results to null if the user clicks "clear"
  ] = useAtom(memberSearchResultsAtom);

  async function initializeSession(memberInfo: MemberSearchResult) {
    setIsLoading(true);

    const initializeSessionRequest: TMemberInfo = {
      enterpriseIndividualIdentifier:
        memberInfo.enterpriseIndividualIdentifier ?? null,
      address: memberInfo.address ?? undefined,
      gender: memberInfo.gender ?? '',
      email: memberInfo.emailAddress ?? '',
      zipCode: memberInfo.zipCode ?? '',
      lob: memberInfo.lineOfBusiness,
      lineOfBusiness: memberInfo.lineOfBusiness,
      phone: memberInfo.phoneNumber ?? '',
      first_name: memberInfo.firstName,
      last_name: memberInfo.lastName,
      DOB: memberInfo.dob ?? '',
      member_id: memberInfo.memberId,
      addrZipCode: memberInfo.zipCode ?? undefined,
      memberType: memberInfo.memberIdType ?? '',
      benefitPlanPlatform: memberInfo.benefitPlanPlatform ?? undefined,
      benefitPlanID: memberInfo.benefitPlanID ?? undefined,
      medSup: memberInfo.medSup ?? false,
      ifp:memberInfo.ifp ?? false,
      referenceId:memberInfo.referenceId ?? '',
    };
    const resp = await API.initializeSession(initializeSessionRequest);
    setMemberInContext(initializeSessionRequest);

    if (resp.redirect_url) {
      if (resp.status === 'success') {
        window.location.href = resp.redirect_url;
      }
    }
    setIsLoading(false);
  }

  /**
   * Given an array of member search results from the API,
   * massage them into the shape of our table data.
   */
  function memberSearchResultsToTableData(
    results: MemberSearchResult[]
  ): TableShape[] {
    return results.map((memberInfo: MemberSearchResult) => {
      return {
        name: capitalizeName(`${memberInfo.firstName} ${memberInfo.lastName}`),
        memberid: memberInfo.memberId,
        dateofbirth: memberInfo.dob ?? '',
        state: memberInfo.state ?? '',
        zipcode: memberInfo.zipCode ?? '',
        lob: memberInfo.lineOfBusiness,
        action: (
          <Button
            onClick={() => {
              initializeSession(memberInfo);
            }}
          >
            Select member
          </Button>
        ),
      };
    });
  }

  // Search has not been performed, illegal state since the parent should prevent this component from being shown
  if (memberSearchResults === null || memberSearchResults === undefined) {
    console.warn(
      'DisplayResults read the memberSearchResults and they were null.'
    );
    return <React.Fragment />;
  }

  // Search has been performed and there were no results
  if (memberSearchResults?.length === 0) {
    return (
      <React.Fragment>
        {isLoading ? (
          <div
            style={{
              zIndex: 999,
              backgroundColor: '#FFFFFF',
              opacity: 0.75,
              position: 'fixed',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              top: 0,
              left: 0,
            }}
          >
            <LoadingSpinner
              size="xl"
              isLoading={isLoading}
              ariaLoadingLabel="Downloading files"
            />
          </div>
        ) : (
          ``
        )}
        <div>
          Showing 0 matches
          <div
            style={{
              padding: '24px',
              marginTop: '15px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <IconSymbol icon="error" color="$core.color.red.110" />
              <Heading size="md" color="$core.color.red.110">
                Member not found.
              </Heading>
            </div>
            <Text fontSize="md" color="#002677" style={{ paddingLeft: '20px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span style={{ margin: '0 30px' }}>•</span>
                {forSearchTerm === 'info'
                  ? 'Confirm you input the correct Member Name and Date of Birth.'
                  : 'Confirm you input the correct Member Type and Member ID.'}
              </span>
            </Text>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                setMemberSearchResults(undefined as NoSearchPerformed);
              }}
              variant="text"
              color="brand"
            >
              Search again
              <IconSymbol icon="arrow_upward" />
            </Button>
          </div>
        </div>
      </React.Fragment>
    );
  }

  // Search has been performed and there are results to show

  return (
    <div>
      {isLoading ? (
        <div
          style={{
            zIndex: 999,
            backgroundColor: '#FFFFFF',
            opacity: 0.75,
            position: 'fixed',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            top: 0,
            left: 0,
          }}
        >
          <LoadingSpinner
            size="xl"
            isLoading={isLoading}
            ariaLoadingLabel="Searching for member"
          />
        </div>
      ) : (
        ``
      )}
      Select a member below to find a community-based organization for them.
      <br />
      <br />
      <WrapperDiv>
        <Table
          title="Table Scroll"
          columns={columns}
          rows={memberSearchResultsToTableData(memberSearchResults)}
        />
      </WrapperDiv>
    </div>
  );
};

DisplayResults.propTypes = {
  forSearchTerm: PropTypes.string.isRequired,
};
